import { ArrowUp, ArrowDown } from '@/assets/icons'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'

type SortableHeaderButtonProps = {
  /** The label text to display in the header */
  label: string
  /** The current sort state: false (not sorted), 'asc', or 'desc' */
  isSorted: false | 'asc' | 'desc'
  /** Callback when the header is clicked to change sort */
  onSort: () => void
  /** Optional label for ascending sort (e.g., "Sort A-Z" or "Sort old to new") */
  ascendingLabel?: string
  /** Optional label for descending sort (e.g., "Sort Z-A" or "Sort new to old") */
  descendingLabel?: string
  /** Whether the default sort direction is descending (true) or ascending (false). Defaults to false (ascending). */
  defaultSortDesc?: boolean
}

/**
 * Sortable Header Button
 *
 * A button component for sortable table headers that displays a sort direction indicator when actively sorted,
 * and shows a tooltip on hover indicating the next sort action (only on non-touch-only devices).
 */
export function SortableHeaderButton({
  label,
  isSorted,
  onSort,
  ascendingLabel = 'Sort Ascending',
  descendingLabel = 'Sort Descending',
  defaultSortDesc = false,
}: SortableHeaderButtonProps) {
  const isTouchOnlyDevice = useIsTouchOnlyDevice()

  // Determine the next sort action label for tooltip/aria-label - shows what will happen on click
  let nextSortActionLabel: string
  if (!isSorted) {
    // Not sorted: show the default sort action
    nextSortActionLabel = defaultSortDesc ? descendingLabel : ascendingLabel
  } else if (isSorted === 'asc') {
    // Currently ascending: clicking will sort descending
    nextSortActionLabel = descendingLabel
  } else {
    // Currently descending: clicking will sort ascending
    nextSortActionLabel = ascendingLabel
  }

  // Include the column name in the aria label for context
  const ariaLabel = `${label}: ${nextSortActionLabel}`

  // Only show the sort icon when actively sorted
  const showIcon = isSorted !== false

  // Choose the correct icon based on sort direction
  const Icon = isSorted === 'asc' ? ArrowUp : ArrowDown

  const button = (
    <button
      onClick={onSort}
      className="flex items-center gap-1.5 px-1 py-0.5 rounded-md min-h-[28px] w-full text-left hover-capable:hover:bg-surface-hover focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus-ring-primary focus-visible:bg-surface-hover min-w-0"
      aria-label={ariaLabel}
    >
      <span className="text-sm text-text-primary whitespace-nowrap tracking-[-0.15px] leading-5 truncate min-w-0">{label}</span>
      {showIcon && (
        <div className="inline-flex items-center justify-center shrink-0 size-6">
          <div className="bg-brand-neutral-200 rounded-full p-1 flex items-center justify-center shrink-0">
            <Icon className="text-text-tertiary size-3.5" aria-hidden />
          </div>
        </div>
      )}
    </button>
  )

  // Only show tooltip on non-touch-only devices
  if (isTouchOnlyDevice) {
    return button
  }

  return (
    <CustomizableTooltip
      title={nextSortActionLabel}
      placement="top-start"
      disableInteractive
      slotProps={{
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, -12] } }],
        },
        tooltip: {
          className: '!bg-surface-inverse !text-text-on-inverse-primary !rounded-xs !px-1 text-2xs font-medium tracking-wide',
        },
      }}
    >
      {button}
    </CustomizableTooltip>
  )
}
